import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';

import { orderSummaryToListQuery } from './filters';
import { orderSummaryCityPageSchema, orderSummaryListResponseSchema } from './schemas';
import type {
  OrderSummaryCityPage,
  OrderSummaryCityParams,
  OrderSummaryListParams,
  OrderSummaryListResponse,
} from './types';

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function orderSummaryRequestQuery(params: Omit<OrderSummaryListParams, 'page' | 'limit'>) {
  const { sortBy = 'orderDate', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy,
    sortOrder: sortOrder.toUpperCase(),
    // Search + the seven panel filters in one mapping — `toListQuery` joins the multi-selects
    // and strips every unset dimension, so an empty panel sends nothing (AC7).
    ...orderSummaryToListQuery(filters),
  };
}

/** Paginated/sorted/searchable Order Summary report — one row per Order.
 *
 *  Defaults to `orderDate` DESC (AC7), matching the backend's own default so the first page is
 *  newest-first even though `useTableState` sends no sort until the admin clicks a header.
 *
 *  `sortOrder` is **uppercased**: `libs/common`'s `SortOrder` enum is `ASC`/`DESC`, and the deployed
 *  orders DTO only tolerates lowercase because it carries an explicit uppercasing `@Transform`.
 *  Sending uppercase is valid whether or not the Reports DTO copies that — same call
 *  `listWarrantyClaims` makes. Do not lowercase this.
 *
 *  `parseContract` (not bare `.parse`) because this contract is new: drift lands in Sentry as
 *  `contract_drift` instead of white-screening the tab. */
export async function listOrderSummary(
  params: OrderSummaryListParams,
): Promise<OrderSummaryListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.reports.orderSummary, {
    params: { page, limit, ...orderSummaryRequestQuery(rest) },
  });
  return parseContract(
    orderSummaryListResponseSchema,
    { data, meta },
    endpoints.reports.orderSummary,
  );
}

/** One page of the City filter's typeahead (AC11: scoped options that load more on scroll or
 *  type). Reports adds **no** meta routes of its own (grilling Q6, backend design §2.4) — this is
 *  the existing orders endpoint, cursor-paged (`{limit, cursor, search}` → `{data, meta}`) unlike
 *  the offset-paged grids, and returning plain strings.
 *
 *  `isShipping=true` scopes it to cities present on visible orders, which is exactly the Order
 *  Summary population. The backend already defaults the flag to `true`; it is sent explicitly so
 *  the scoping is visible at the call site.
 *
 *  Deliberately NOT `useOrderCities()`: that helper reads one un-searched page of 20 and discards
 *  `meta`, so it can neither search nor page — AC11 needs both. */
export async function listOrderSummaryCities({
  search,
  cursor,
  limit,
}: OrderSummaryCityParams): Promise<OrderSummaryCityPage> {
  const page = await apiClient.getList(endpoints.orders.metaCities, {
    params: { search, limit, isShipping: true, ...(cursor ? { cursor } : {}) },
  });
  // `parseContract`, like the list call above: the cursor envelope on this endpoint is not what the
  // shipped `listOrderCities` reads (it takes `data` and drops `meta`), so drift here would
  // otherwise surface as a silently empty dropdown rather than a greppable Sentry signal.
  return parseContract(orderSummaryCityPageSchema, page, endpoints.orders.metaCities);
}
